import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { CheckCircle, Mail, MessageCircle, Send, UserCheck } from 'react-feather';
import Screen from '../Layouts/Screen/Screen';
import Spinner from '../../global-components/src/components/spinner/spiner';
import { useAuth, useMessage } from '../../global-components/src';
import { errorMessage } from '../../global-components/src/utils/text';
import { api } from '../../services/api';
import './moderator-actions.css';
import './pacoca-papo-testers.css';

interface PapoTester {
    id: number;
    email: string;
    account_email: string | null;
    created_at: string;
    released_at: string | null;
    delivery_method: 'pacoca' | 'email' | null;
    user: {
        id: number;
        name: string;
        user_name: string;
    } | null;
}

interface ReleaseResponse {
    tester?: Pick<PapoTester, 'delivery_method'>;
}

const PacocaPapoTesters = () => {
    const { token } = useAuth();
    const { setMessage } = useMessage();
    const [testers, setTesters] = useState<PapoTester[]>([]);
    const [email, setEmail] = useState('');
    const [accountEmail, setAccountEmail] = useState('');
    const [accountEmails, setAccountEmails] = useState<Record<number, string>>({});
    const [loading, setLoading] = useState(true);
    const [releasingTesterId, setReleasingTesterId] = useState<number | null>(null);

    const loadTesters = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api(token).get<PapoTester[]>('/pacoca-tools/papo-testers');
            setTesters(data);
        } catch (error) {
            setMessage(errorMessage(error), 'error');
        } finally {
            setLoading(false);
        }
    }, [setMessage, token]);

    useEffect(() => {
        void loadTesters();
    }, [loadTesters]);

    const confirmationMessage = (approvedEmail: string, targetAccountEmail?: string) => {
        if (targetAccountEmail) {
            return `Confirmar que ${approvedEmail} foi adicionado ao teste? Se ${targetAccountEmail} for uma conta Paçoca, ela receberá uma notificação, um push e um e-mail informando que o acesso foi liberado para ${approvedEmail}. Caso contrário, enviaremos a aprovação por e-mail para ${approvedEmail}.`;
        }

        return `Confirmar que ${approvedEmail} foi adicionado ao teste? Se houver uma conta Paçoca com esse e-mail, ela receberá uma notificação, um push e um e-mail. Caso não haja, enviaremos a aprovação por e-mail para ${approvedEmail}.`;
    };

    const successMessage = (approvedEmail: string, deliveryMethod?: PapoTester['delivery_method']) => (
        deliveryMethod === 'email'
            ? `A aprovação foi enviada por e-mail para quem solicitou o acesso a ${approvedEmail}.`
            : `Acesso liberado. A conta Paçoca receberá notificação, push e e-mail sobre ${approvedEmail}.`
    );

    const submitAccess = async (event: FormEvent) => {
        event.preventDefault();
        const normalizedEmail = email.trim();
        const normalizedAccountEmail = accountEmail.trim();
        if (!normalizedEmail) return;

        if (!window.confirm(confirmationMessage(normalizedEmail, normalizedAccountEmail || undefined))) return;

        setReleasingTesterId(0);
        try {
            const { data } = await api(token).post<ReleaseResponse>('/pacoca-tools/papo-testers', {
                email: normalizedEmail,
                account_email: normalizedAccountEmail || null,
            });
            setMessage(successMessage(normalizedEmail, data.tester?.delivery_method), 'success');
            setEmail('');
            setAccountEmail('');
            await loadTesters();
        } catch (error) {
            setMessage(errorMessage(error), 'error');
        } finally {
            setReleasingTesterId(null);
        }
    };

    const releaseAccess = async (tester: PapoTester, rawAccountEmail = '') => {
        const normalizedAccountEmail = rawAccountEmail.trim();

        if (!window.confirm(confirmationMessage(tester.email, normalizedAccountEmail || undefined))) return;

        setReleasingTesterId(tester.id);
        try {
            const { data } = await api(token).post<ReleaseResponse>(`/pacoca-tools/papo-testers/${tester.id}/release`, {
                account_email: normalizedAccountEmail || null,
            });
            setMessage(successMessage(tester.email, data.tester?.delivery_method), 'success');
            await loadTesters();
        } catch (error) {
            setMessage(errorMessage(error), 'error');
        } finally {
            setReleasingTesterId(null);
        }
    };

    return (
        <Screen title="Acesso de teste do Paçoca Papo" paddingTopTitle>
            <Helmet>
                <title>Paçoca - Acesso de teste do Paçoca Papo</title>
            </Helmet>

            <div className="moderator-action-page">
                <section className="moderator-action-card moderator-papo-card">
                    <div className="moderator-action-heading moderator-papo-heading">
                        <div>
                            <h2><MessageCircle size={21} /> Acesso de teste do Paçoca Papo</h2>
                            <p>Libere os e-mails já incluídos no Google Play. Quando não houver uma conta Paçoca, a confirmação seguirá por e-mail para quem solicitou o acesso.</p>
                        </div>
                        <span className="moderator-action-count" aria-label={`${testers.length} solicitações`}>{testers.length}</span>
                    </div>

                    <form className="moderator-papo-form" onSubmit={submitAccess}>
                        <div className="moderator-action-field">
                            <label htmlFor="papo-tester-email">E-mail aprovado no Google Play</label>
                            <input
                                id="papo-tester-email"
                                className="form-control"
                                type="email"
                                required
                                value={email}
                                onChange={event => setEmail(event.target.value)}
                                placeholder="usuario@gmail.com"
                            />
                        </div>
                        <div className="moderator-action-field">
                            <label htmlFor="papo-tester-account-email">Conta Paçoca para receber o aviso <span>(opcional)</span></label>
                            <input
                                id="papo-tester-account-email"
                                className="form-control"
                                type="email"
                                value={accountEmail}
                                onChange={event => setAccountEmail(event.target.value)}
                                placeholder="conta@exemplo.com"
                            />
                            <small>Deixe em branco para usar a conta correspondente ou o e-mail de quem solicitou.</small>
                        </div>
                        <button className="btn btn-primary moderator-papo-release-button moderator-papo-submit-button" type="submit" disabled={releasingTesterId === 0}>
                            {releasingTesterId === 0 ? <Spinner /> : <Send size={17} />}
                            Adicionar e liberar
                        </button>
                    </form>

                    <p className="moderator-papo-test-link">
                        <Mail size={15} aria-hidden="true" />
                        Link incluído na aprovação: <a href="https://play.google.com/apps/testing/com.joaoalves.pacoca.chat" target="_blank" rel="noreferrer">Teste do Paçoca Papo no Google Play</a>
                    </p>

                    <div className="moderator-papo-list" aria-live="polite">
                        {loading ? (
                            <div className="moderator-action-loading"><Spinner /></div>
                        ) : testers.length === 0 ? (
                            <p className="moderator-action-empty">Nenhuma solicitação de teste ainda.</p>
                        ) : testers.map(tester => {
                            const hasPacocaAccount = Boolean(tester.user);
                            const isReleasedByEmail = tester.delivery_method === 'email';

                            return (
                                <article className="moderator-papo-row" key={tester.id}>
                                    <div className="moderator-papo-info">
                                        <div className="moderator-papo-email">
                                            <Mail size={17} aria-hidden="true" />
                                            <strong>{tester.email}</strong>
                                        </div>
                                        {hasPacocaAccount ? (
                                            <p className="moderator-papo-account">
                                                <UserCheck size={15} aria-hidden="true" />
                                                {tester.user?.name} · @{tester.user?.user_name}
                                            </p>
                                        ) : (
                                            <p className="moderator-papo-email-delivery">
                                                <Mail size={15} aria-hidden="true" />
                                                Sem conta Paçoca: a aprovação será enviada para este e-mail.
                                            </p>
                                        )}
                                        {tester.account_email && <small>Conta Paçoca: {tester.account_email}</small>}
                                    </div>

                                    {tester.released_at ? (
                                        <span className="moderator-papo-status is-released">
                                            {isReleasedByEmail ? <Mail size={14} /> : <CheckCircle size={14} />}
                                            {isReleasedByEmail ? 'Aprovação enviada por e-mail' : 'Acesso liberado no Paçoca'}
                                        </span>
                                    ) : (
                                        <div className="moderator-papo-actions">
                                            <button
                                                className="btn btn-primary moderator-papo-release-button"
                                                type="button"
                                                disabled={releasingTesterId === tester.id}
                                                onClick={() => void releaseAccess(tester)}
                                            >
                                                {releasingTesterId === tester.id ? <Spinner /> : hasPacocaAccount ? <UserCheck size={17} /> : <Mail size={17} />}
                                                {hasPacocaAccount ? 'Liberar acesso' : 'Enviar aprovação por e-mail'}
                                            </button>

                                            <details className="moderator-papo-alternate-account">
                                                <summary>Usar outra conta Paçoca</summary>
                                                <form
                                                    aria-label={`Escolher conta Paçoca para ${tester.email}`}
                                                    onSubmit={event => {
                                                        event.preventDefault();
                                                        void releaseAccess(tester, accountEmails[tester.id] ?? '');
                                                    }}
                                                >
                                                    <div className="moderator-action-field">
                                                        <label htmlFor={`papo-tester-account-email-${tester.id}`}>E-mail da conta Paçoca</label>
                                                        <input
                                                            id={`papo-tester-account-email-${tester.id}`}
                                                            className="form-control"
                                                            type="email"
                                                            required
                                                            value={accountEmails[tester.id] ?? ''}
                                                            onChange={event => setAccountEmails(currentEmails => ({
                                                                ...currentEmails,
                                                                [tester.id]: event.target.value,
                                                            }))}
                                                            placeholder="conta@exemplo.com"
                                                        />
                                                    </div>
                                                    <button className="btn btn-light moderator-papo-alternate-button" type="submit" disabled={releasingTesterId === tester.id}>
                                                        <UserCheck size={16} /> Liberar nesta conta
                                                    </button>
                                                </form>
                                            </details>
                                        </div>
                                    )}
                                </article>
                            );
                        })}
                    </div>
                </section>
            </div>
        </Screen>
    );
};

export default PacocaPapoTesters;
